import { ssGet, ssSet } from "./auth";
import { registerAppServiceWorker } from "./serviceWorker";

/**
 * Има ли на сървъра по-нов билд от този, който прозорецът изпълнява. Един
 * източник за двамата потребители — модала „Нова версия"
 * (`components/VersionCheckNotifier`) и тихото презареждане на ПИН/login
 * екрана (`hooks/useReloadOnNewVersion`). Виж docs/pwa.md.
 *
 * SPA-то върти стария бъндъл в отворения прозорец до ПЪЛНО презареждане на
 * документа: вход и изход са `navigate()` (React Router) и НЕ помагат, а в
 * инсталирано PWA няма F5 и иконата само фокусира вече отворения прозорец.
 *
 * СИГНАЛЪТ Е ХЕШЪТ НА ВХОДНИЯ БЪНДЪЛ (`/assets/index-<hash>.js`), не
 * `VITE_APP_VERSION`: всеки `vite build` дава нов хеш, тоест нов image в k8s
 * се засича сам. `VITE_APP_VERSION` идва отвън, от Helm (README.md §Env
 * променливи) — сгрешена или непроменена стойност там прави проверката тиха
 * нула, без нищо да гръмне.
 */

// Пази от примка: ако след презареждането билдовете пак се разминат (частичен
// rollout — два pod-а с различни image-и зад балансьора), не презареждаме за
// същия билд втори път.
const RELOADED_FOR_KEY = "app:reloadedFor";

// Обикалянето на service worker-а: `/index.html` Е precache-нат, тоест
// `precacheAndRoute` би върнал кешираното копие cache-first. Workbox не
// игнорира произволни query параметри, затова с този суфикс URL-ът не мачва
// нито един route и заявката отива право на мрежата.
const CACHE_BUSTER = "?build-probe=";

// Таван на чакането новият service worker да свали билда (~5 MB precache) —
// при бавна мрежа не държим модала „Нова версия" скрит безкрай.
const WORKER_INSTALL_TIMEOUT_MS = 60_000;

/**
 * Отпечатъкът на билда, който ТОЗИ документ е заредил — от `<script>` тага,
 * поставен от Vite в `index.html`. Празен в dev (там входът е `/src/main.tsx`,
 * не хеширан asset) — тогава проверка не се прави.
 */
export function getRunningBuildId(): string {
  const entryScript = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/"]',
  );
  return entryScript?.getAttribute("src") ?? "";
}

/** Отпечатъкът на билда, качен на сървъра; null при мрежова грешка. */
async function fetchServerBuildId(): Promise<string | null> {
  try {
    const response = await fetch(`/index.html${CACHE_BUSTER}${Date.now()}`, {
      cache: "no-store",
    });
    if (!response.ok) return null;
    const html = await response.text();
    const entryMatch = html.match(
      /<script[^>]+src="(\/assets\/[^"]+\.js)"[^>]*>/,
    );
    return entryMatch ? entryMatch[1] : null;
  } catch {
    // Мрежова грешка (офлайн, рестарт на pod-а) — пропускаме тази проверка.
    return null;
  }
}

/**
 * Кара браузъра да провери за нов `sw.js` и изчаква новия worker да се
 * инсталира и поеме (`skipWaiting` + `clientsClaim` в `src/sw.ts`). Без това
 * браузърът научава за новия worker чак при самото презареждане: дотогава
 * отговаря старият, а неговият precache държи стария билд — резервът при
 * бавна мрежа (timeout на навигацията) би заредил пак него.
 */
async function waitForNewServiceWorker(): Promise<void> {
  const registration = await registerAppServiceWorker();
  if (!registration) return;
  try {
    await registration.update();
  } catch {
    // Мрежова грешка при проверката на sw.js — презареждането пак ще мине
    // през мрежата, просто без готов precache като резерв.
    return;
  }
  const newWorker = registration.installing ?? registration.waiting;
  if (!newWorker) return;
  await new Promise<void>((resolve) => {
    const finish = () => {
      window.clearTimeout(timeout);
      newWorker.removeEventListener("statechange", onStateChange);
      resolve();
    };
    // „redundant" = инсталацията се провали (напр. asset 404 по време на
    // деплоя) — не чакаме повече, модалът пак се показва.
    const onStateChange = () => {
      if (newWorker.state === "activated" || newWorker.state === "redundant")
        finish();
    };
    const timeout = window.setTimeout(finish, WORKER_INSTALL_TIMEOUT_MS);
    newWorker.addEventListener("statechange", onStateChange);
    onStateChange();
  });
}

/**
 * Билдът на сървъра, ако е РАЗЛИЧЕН от заредения; иначе null. Връща го чак
 * когато новият service worker е готов, тоест презареждането веднага след
 * това зарежда новия билд от първия път.
 */
export async function checkForNewVersion(): Promise<string | null> {
  const runningBuildId = getRunningBuildId();
  if (!runningBuildId) return null;
  const serverBuildId = await fetchServerBuildId();
  if (!serverBuildId || serverBuildId === runningBuildId) return null;
  await waitForNewServiceWorker();
  return serverBuildId;
}

/**
 * Презарежда документа заради билд `serverBuildId`. Вика се САМО от екрани
 * без отворена работа (ПИН клавиатурата, /login) — на каса с отворена сметка
 * автоматичното презареждане е забранено (docs/pwa.md); там пита човекът
 * през `VersionCheckNotifier`.
 */
export function reloadForVersion(serverBuildId: string): void {
  if (ssGet(RELOADED_FOR_KEY) === serverBuildId) return;
  ssSet(RELOADED_FOR_KEY, serverBuildId);
  window.location.reload();
}
